import colors from '@/design/colors';
import { FiCheck, FiMinus } from 'react-icons/fi';
import styled from 'styled-components';

import { SelectionControlProps } from './types';

/**
 * Selection Control default attributes and behaviours
 */
const SelectionControlSizes = {
  normal: 22,
  small: 16,
};

const SelectionControlPaddings = {
  normal: 4,
  small: 2,
};

export const Marker = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Marked = styled(FiCheck)`
  stroke-width: 3;
`;

export const HalfMarked = styled(FiMinus)`
  stroke-width: 3;
`;

type SelectionControlColorKey =
  | 'unmarked'
  | 'unmarked:disabled'
  | 'marked'
  | 'marked:disabled';

const SelectionControlColorMap: Record<
  SelectionControlColorKey,
  { fill: string; stroke: string }
> = {
  unmarked: {
    fill: colors.white,
    stroke: colors.gray100,
  },
  'unmarked:disabled': {
    fill: colors.whiteAlpha50,
    stroke: colors.gray100,
  },
  marked: {
    fill: colors.gray50,
    stroke: colors.purple100,
  },
  'marked:disabled': {
    fill: colors.gray100,
    stroke: colors.blackAlpha100,
  },
};

const getSelectionControlColors = ({
  state,
  disabled,
}: SelectionControlProps): { fill: string; stroke: string } => {
  const stateKey = state !== 'unmarked' ? 'marked' : 'unmarked';
  const disabledSuffix = disabled ? ':disabled' : '';
  const key = `${stateKey}${disabledSuffix}` as SelectionControlColorKey;

  return SelectionControlColorMap[key];
};

export const SelectionControlContainer = styled.div<SelectionControlProps>`
  /* Layout */
  border: 1px solid ${colors.gray100};
  display: flex;
  align-items: center;
  justify-content: stretch;
  padding: ${({ size }) => SelectionControlPaddings[size]}px;
  height: ${({ size }) => SelectionControlSizes[size]}px;
  width: ${({ size }) => SelectionControlSizes[size]}px;
  min-width: ${({ size }) => SelectionControlSizes[size]}px;

  /* Interactions */
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

  /* Colors */
  background-color: ${props => getSelectionControlColors(props).fill};
  border-color: ${props => getSelectionControlColors(props).stroke};

  /* Hover colors */
  :hover:not([data-disabled='true']),
  &[data-focus='true']:not([data-disabled='true']) {
    background-color: ${colors.white};
    border-color: ${colors.purple100};
  }

  ${Marked}, ${HalfMarked} {
    color: ${props => getSelectionControlColors(props).stroke};
    width: ${({ size }) => (size === 'normal' ? '13px' : '11px')};
    height: ${({ size }) => (size === 'normal' ? '13px' : '11px')};
  }
`;

/**
 * Radio
 */

export const RadioContainer = styled(SelectionControlContainer)`
  border-radius: 100%;
`;

/**
 * Checkbox
 */
export const CheckboxContainer = styled(SelectionControlContainer)`
  border-radius: 2px;
`;
